import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Info, Plus, AlertTriangle } from 'lucide-react';
import type { Mailbox, Quota } from '../../../shared/api/types';
import { Button } from '../../../shared/ui/Button';
import { Tooltip } from '../../../shared/ui/Tooltip';
import { useOnboardingAnchor } from '../../onboarding/useOnboardingAnchor';
import { useQuotaResetInfo } from '../useQuotaResetInfo';

/** Фолбэк лимита трафика, если API ещё не ответил (реальное значение — quota.traffic). */
const DEFAULT_TRAFFIC_MB = 50;

export interface QuotaHeaderCardProps {
  mailboxes: Mailbox[];
  quota?: Quota;
  onAdd: () => void;
  onCleanup: () => void;
  onShowPaused: () => void;
}

export function QuotaHeaderCard({
  mailboxes,
  quota,
  onAdd,
  onCleanup,
  onShowPaused,
}: QuotaHeaderCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addButtonAnchor = useOnboardingAnchor('mailbox.addButton');
  const quotaAnchor = useOnboardingAnchor('mailbox.quotaBlock');
  const forwardingAnchor = useOnboardingAnchor('mailbox.forwardingBanner');

  // Значения трафика теперь приходят из API (config/subscription-driven), с фолбэком
  // на сумму по ящикам, пока /quota не ответил. §promt-78 §B.
  const limitMb = quota?.traffic.limitMb ?? DEFAULT_TRAFFIC_MB;
  const usedMb = quota?.traffic.usedMb ?? mailboxes.reduce((sum, m) => sum + (m.trafficUsedMb || 0), 0);
  const period = quota?.traffic.period ?? 'month';
  const periodText = t(`quota.period.${period}`);
  const resetText = t(`quota.periodReset.${period}`);
  const remainingMb = Math.max(0, limitMb - usedMb);
  const pct = limitMb > 0 ? Math.min(100, Math.round((usedMb / limitMb) * 100)) : 0;
  const { resetDate, resetLabel } = useQuotaResetInfo(quota);
  const createLimitReached = quota ? quota.active.used >= quota.active.limit : false;
  const paused = remainingMb <= 0;
  const warning = !paused && remainingMb < 5;

  return (
    <section className="rounded-2xl bg-surface p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[240px] flex-1">
          <h2 className="font-semibold text-fg">{t('mailbox.headerCard')}</h2>
          <div {...quotaAnchor}>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-fg-muted">
              {t('mailbox.quotaRemaining', {
                remaining: remainingMb.toFixed(remainingMb % 1 === 0 ? 0 : 1),
                total: limitMb,
                period: periodText,
              })}
              <Tooltip content={t('mailbox.quotaTooltip', { total: limitMb, period: periodText, reset: resetText })}>
                <button type="button" className="text-accent" aria-label="info">
                  <Info className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </p>
            <div className="mt-2 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-accent-soft">
              <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${String(pct)}%` }} />
            </div>
          </div>
        </div>

        {/* На мобильном действие занимает всю ширину карточки, на десктопе — по содержимому
            справа от блока квоты (замечание 3, promt-86). */}
        {createLimitReached ? (
          <Tooltip
            variant="light"
            content={
              <span>
                {t('mailbox.limitTooltipBefore')}
                <button
                  type="button"
                  onClick={onCleanup}
                  className="font-medium text-brand underline-offset-2 hover:underline"
                >
                  {t('mailbox.limitTooltipAction')}
                </button>
                {t('mailbox.limitTooltipAfter')}
              </span>
            }
          >
            <button
              type="button"
              onClick={onCleanup}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-page px-4 py-2.5 text-sm font-medium text-fg-muted hover:text-fg sm:w-auto"
            >
              {t('mailbox.limitReached')}
              <Info className="h-3.5 w-3.5 fill-fg-muted text-page" />
            </button>
          </Tooltip>
        ) : (
          <Button className="w-full sm:w-auto" onClick={onAdd} {...addButtonAnchor}>
            <Plus className="h-4 w-4" /> {t('mailbox.addMailbox').replace('+ ', '')}
          </Button>
        )}
      </div>

      {warning && (
        <div className="mt-4 flex gap-3 rounded-xl border border-info/30 bg-info-soft p-3.5">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-info" />
          <div>
            <p className="text-sm font-semibold text-fg">{t('quota.warningTitle')}</p>
            <p className="mt-0.5 text-sm text-fg-muted">{t('quota.warningBody')}</p>
          </div>
        </div>
      )}

      {paused && (
        <div className="mt-4 flex gap-3 rounded-xl border border-warning/40 bg-warning-soft p-3.5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <div>
            <p className="text-sm font-semibold text-fg">
              {t('quota.pausedTitle', { date: resetDate })}
            </p>
            <p className="mt-0.5 text-sm text-fg-muted">{resetLabel}</p>
            <button
              type="button"
              onClick={onShowPaused}
              className="mt-1 text-sm font-medium text-brand"
            >
              {t('quota.pausedDetails')}
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => void navigate({ to: '/forwarding' })}
        className="mt-3 flex w-full items-center justify-between rounded-xl bg-page px-4 py-3 text-left"
        {...forwardingAnchor}
      >
        <span className="text-sm text-fg-muted">{t('mailbox.forwardingBanner')}</span>
        <span className="text-sm font-medium text-brand">{t('mailbox.forwardingSetup')}</span>
      </button>
    </section>
  );
}
